import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery, useSubscription } from '@apollo/client';
import {
  BOOKMARK_MESSAGE,
  DELETE_MESSAGE,
  EDIT_MESSAGE,
  FORWARD_MESSAGE,
  GET_MESSAGES_PAGINATED,
  GET_MY_PROFILE,
  LIST_BOOKMARKS,
  LIST_PINNED_MESSAGES,
  ON_NEW_MESSAGE,
  ON_MESSAGE_UPDATE,
  ON_REACTION_UPDATE,
  PIN_MESSAGE,
  UNBOOKMARK_MESSAGE,
  UNPIN_MESSAGE
} from '../../graphql/operations';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import ChatHeader from './ChatHeader';
import ForwardMessageModal from './ForwardMessageModal';
import MessageListV2 from './MessageList.new';
import MessageComposer from './MessageComposer';
import PinnedMessagesBanner from './PinnedMessagesBanner';
import ThreadPanel from './ThreadPanel';
import { toIsoTimestamp } from './time';
import { reconcileMessagesWithServer } from './reconcileMessages';
import { applyMessageUpdateToMessages } from './messageUpdates';
import { mergeOnNewMessageIntoExisting } from './onNewMessageMerge';
import { consumeOptimisticThinkingForMessage } from './optimisticThinkingQueue';
import { applyThreadParentPatch } from './threadParentPatch';
import { mergeMessageIntoCache } from './messageCacheSync';
import { parseReactionCounts } from './reactionCounts';
import { mergeServerAndLocalMessages } from './mergeServerAndLocalMessages';
import { MSG_PARAM, parseMessageDeepLink } from './messageDeepLink';
import { resolveQuickReactTokens } from './quickReactTokens';
import './UnifiedChatPanel.css';

const HYDRATION_RETRY_BACKOFFS_MS = [300, 1000, 3000];

/**
 * UnifiedChatPanel — the main chat view, composing ChatHeader, MessageList,
 * MessageComposer, and ThreadPanel.
 *
 * Handles message pagination, real-time subscriptions, and thread state.
 */
function UnifiedChatPanel({ conversation }) {
  const { markRead } = useContext(WorkspaceContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeThread, setActiveThread] = useState(null);
  const lastDeepLinkScrolledRef = useRef('');
  // Edit-message mutation. The optimistic merge happens via the existing
  // onNewMessage subscription path (schema now subscribes to both
  // sendMessage and editMessage), so we don't need an optimisticResponse
  // here — the same merge-by-id logic handles inbound edits naturally.
  const [editMessageMutation] = useMutation(EDIT_MESSAGE);
  const handleEditMessage = useCallback(async (messageId, conversationIdArg, content) => {
    const result = await editMessageMutation({
      variables: { messageId, conversationId: conversationIdArg, content }
    });
    return result?.data?.editMessage || null;
  }, [editMessageMutation]);
  const [deleteMessageMutation] = useMutation(DELETE_MESSAGE);
  const apolloClient = useApolloClient();
  const [allMessages, setAllMessages] = useState([]);
  const [confirmPollUntilMs, setConfirmPollUntilMs] = useState(0);
  const lastHydratedConversationRef = useRef(null);
  const hydrationRetryByConversationRef = useRef(new Map());
  const pendingOptimisticThinkingIdsRef = useRef([]);
  const consumedOptimisticThinkingByMessageIdRef = useRef(new Map());

  // Current user
  const { data: profileData } = useQuery(GET_MY_PROFILE);
  const currentUserId = profileData?.getMyProfile?.username || null;
  // KAP-310 — per-user quick-react chip trio. `resolveQuickReactTokens`
  // falls back to the default `check/eyes/raised_hands` if the persisted
  // value is missing, malformed, or carries an unknown token. The hover
  // toolbar receives a final 3-token array; no validation needed downstream.
  const quickReactTokens = resolveQuickReactTokens(profileData?.getMyProfile?.quickReactChips);

  // KAP-311.2 — pin / unpin. Both mutations return the updated Message;
  // optimistic responses flip pinnedAt/pinnedBy on the cached bubble
  // immediately, and the `update` block also patches the cached
  // `listPinnedMessages` query so the banner stays in lockstep with the
  // bubble's pin badge. Cross-user broadcast lands via the onNewMessage
  // subscription handler below (see "KAP-311.2 cross-user sync").
  const [pinMessageMutation] = useMutation(PIN_MESSAGE);
  const [unpinMessageMutation] = useMutation(UNPIN_MESSAGE);
  const [forwardMessageMutation] = useMutation(FORWARD_MESSAGE);
  const [bookmarkMessageMutation] = useMutation(BOOKMARK_MESSAGE);
  const [unbookmarkMessageMutation] = useMutation(UNBOOKMARK_MESSAGE);
  // KAP-311.3 — load the user's bookmark list once per panel mount. Bookmarks
  // are tiny (~tens) so a single fetch with limit 100 covers the kebab
  // "Bookmark message" / "Remove bookmark" label state for every bubble.
  // Toggle mutations write back via cache.modify, so the Set stays in sync
  // without a refetch.
  const { data: bookmarksData } = useQuery(LIST_BOOKMARKS, {
    variables: { limit: 100 },
    fetchPolicy: 'cache-and-network'
  });
  const bookmarkedMessageIds = useMemo(() => {
    const items = bookmarksData?.listBookmarks?.items || [];
    return new Set(items.map((b) => b.messageId).filter(Boolean));
  }, [bookmarksData]);

  // KAP-311.1 — single modal owned at the panel level so every bubble shares
  // it. MessageBubble's onForwardMessage callback receives the source Message
  // and stashes it here; the modal renders when this is non-null and the
  // backend mutation fires from inside the modal's onSubmit handler.
  const [pendingForwardMessage, setPendingForwardMessage] = useState(null);

  const buildOptimisticMessage = useCallback((messageId, conversationIdArg, overrides) => ({
    __typename: 'Message',
    id: messageId,
    conversationId: conversationIdArg,
    content: '',
    role: 'user',
    senderId: currentUserId || null,
    senderDisplayName: null,
    threadParentId: null,
    threadReplyCount: 0,
    threadLastReplyAt: null,
    reactionCounts: null,
    widgetType: null,
    widgetData: null,
    widgetActionUserId: null,
    isComplete: true,
    createdAt: null,
    editedAt: null,
    deletedAt: null,
    deletedBy: null,
    pinnedAt: null,
    pinnedBy: null,
    forwardedFrom: null,
    // Slack-parity thread-echo fields. Must be present on every
    // optimistic write so the shape matches the live mutation response
    // (which carries these from buildMessageResponse) — otherwise
    // Apollo's cache merge complains and the cached values for these
    // fields get clobbered to undefined.
    repliedToThreadParentId: null,
    repliedToThreadParentPreview: null,
    alsoSentToChannel: null,
    attachments: [],
    ...overrides
  }), [currentUserId]);

  // KAP-313 — soft-delete. Same merge-by-id path as editMessage: schema
  // subscribes onNewMessage to deleteMessage, so the broadcast naturally
  // patches the cached row. We add an optimistic response so the bubble
  // flips to "This message was deleted" the instant the user confirms.
  // Uses the shared buildOptimisticMessage helper above so the shape
  // exactly matches the live mutation response — Apollo's cache-merge
  // contract complains if any selected field is missing.
  const handleDeleteMessage = useCallback(async (messageId, conversationIdArg) => {
    const result = await deleteMessageMutation({
      variables: { messageId, conversationId: conversationIdArg },
      optimisticResponse: {
        deleteMessage: buildOptimisticMessage(messageId, conversationIdArg, {
          deletedAt: new Date().toISOString(),
          deletedBy: currentUserId || null
        })
      }
    });
    return result?.data?.deleteMessage || null;
  }, [deleteMessageMutation, currentUserId, buildOptimisticMessage]);

  const handlePinMessage = useCallback(async (messageId, conversationIdArg) => {
    const now = new Date().toISOString();
    const result = await pinMessageMutation({
      variables: { messageId, conversationId: conversationIdArg },
      optimisticResponse: {
        pinMessage: buildOptimisticMessage(messageId, conversationIdArg, {
          pinnedAt: now,
          pinnedBy: currentUserId || null
        })
      },
      update: (cache, { data }) => {
        const pinned = data?.pinMessage;
        if (!pinned) return;
        // Append (or refresh) the pinned message in listPinnedMessages so the
        // banner shows it without a refetch. listPinnedMessages returns
        // [Message!]! sorted oldest pin first; new pin goes at the end.
        try {
          const existing = cache.readQuery({
            query: LIST_PINNED_MESSAGES,
            variables: { conversationId: conversationIdArg }
          });
          const current = existing?.listPinnedMessages || [];
          const filtered = current.filter((m) => m.id !== pinned.id);
          cache.writeQuery({
            query: LIST_PINNED_MESSAGES,
            variables: { conversationId: conversationIdArg },
            data: { listPinnedMessages: [...filtered, pinned] }
          });
        } catch (_err) {
          // Cache miss → banner's own query will hydrate from network.
        }
      }
    });
    return result?.data?.pinMessage || null;
  }, [pinMessageMutation, buildOptimisticMessage, currentUserId]);

  const handleOpenForwardModal = useCallback((message) => {
    if (!message) return;
    setPendingForwardMessage(message);
  }, []);

  const handleCloseForwardModal = useCallback(() => {
    setPendingForwardMessage(null);
  }, []);

  const handleForwardSubmit = useCallback(async ({ sourceMessage, targetConversationId, note }) => {
    if (!sourceMessage) throw new Error('Source message missing');
    if (!targetConversationId) throw new Error('Select a conversation');
    await forwardMessageMutation({
      variables: {
        sourceMessageId: sourceMessage.id,
        sourceConversationId: sourceMessage.conversationId,
        targetConversationId,
        note: note || null
      }
    });
  }, [forwardMessageMutation]);

  // KAP-311.3 — bookmark + unbookmark. Apollo cache.modify keeps
  // LIST_BOOKMARKS (the BookmarksView source and our bookmarkedMessageIds
  // projection) in lockstep without a refetch on toggle.
  const handleBookmarkMessage = useCallback(async (messageId, conversationIdArg) => {
    const result = await bookmarkMessageMutation({
      variables: { messageId, conversationId: conversationIdArg },
      update: (cache, { data }) => {
        const bookmark = data?.bookmarkMessage;
        if (!bookmark) return;
        try {
          const existing = cache.readQuery({
            query: LIST_BOOKMARKS,
            variables: { limit: 100 }
          });
          const current = existing?.listBookmarks?.items || [];
          const filtered = current.filter((row) => row.messageId !== bookmark.messageId);
          cache.writeQuery({
            query: LIST_BOOKMARKS,
            variables: { limit: 100 },
            data: {
              listBookmarks: {
                ...existing.listBookmarks,
                // Server orders newest-first (ScanIndexForward: false), so
                // prepend the fresh bookmark.
                items: [bookmark, ...filtered]
              }
            }
          });
        } catch (_err) {
          // Cache miss → next listBookmarks query refetches from network.
        }
      }
    });
    return result?.data?.bookmarkMessage || null;
  }, [bookmarkMessageMutation]);

  const handleUnbookmarkMessage = useCallback(async (messageId, conversationIdArg) => {
    await unbookmarkMessageMutation({
      variables: { messageId, conversationId: conversationIdArg },
      update: (cache) => {
        try {
          const existing = cache.readQuery({
            query: LIST_BOOKMARKS,
            variables: { limit: 100 }
          });
          const current = existing?.listBookmarks?.items || [];
          cache.writeQuery({
            query: LIST_BOOKMARKS,
            variables: { limit: 100 },
            data: {
              listBookmarks: {
                ...existing.listBookmarks,
                items: current.filter((row) => row.messageId !== messageId)
              }
            }
          });
        } catch (_err) {
          // Cache miss → next listBookmarks query refetches from network.
        }
      }
    });
  }, [unbookmarkMessageMutation]);

  const handleUnpinMessage = useCallback(async (messageId, conversationIdArg) => {
    const result = await unpinMessageMutation({
      variables: { messageId, conversationId: conversationIdArg },
      optimisticResponse: {
        unpinMessage: buildOptimisticMessage(messageId, conversationIdArg, {
          pinnedAt: null,
          pinnedBy: null
        })
      },
      update: (cache, { data }) => {
        const unpinned = data?.unpinMessage;
        if (!unpinned) return;
        try {
          const existing = cache.readQuery({
            query: LIST_PINNED_MESSAGES,
            variables: { conversationId: conversationIdArg }
          });
          const current = existing?.listPinnedMessages || [];
          cache.writeQuery({
            query: LIST_PINNED_MESSAGES,
            variables: { conversationId: conversationIdArg },
            data: { listPinnedMessages: current.filter((m) => m.id !== unpinned.id) }
          });
        } catch (_err) {
          // Cache miss → banner's own query will hydrate from network.
        }
      }
    });
    return result?.data?.unpinMessage || null;
  }, [unpinMessageMutation, buildOptimisticMessage]);

  // Admin viewer check — drives MessageBubble's `isAdmin` prop so the
  // Delete affordance shows for non-senders who have admin role.
  const isAdminViewer = Array.isArray(profileData?.getMyProfile?.roles)
    ? profileData.getMyProfile.roles.some((r) => r === 'admin' || r === 'system_admin')
    : false;

  // Paginated messages query — network-only ensures fresh data on every conversation switch
  const { data, loading, error, fetchMore, refetch } = useQuery(GET_MESSAGES_PAGINATED, {
    variables: { conversationId: conversation.id, limit: 50 },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const pageInfo = data?.getMessagesPaginated;
  const hasMore = pageInfo?.hasMore || false;
  const nextCursor = pageInfo?.nextCursor || null;
  const conversationId = conversation.id;
  const conversationType = String(conversation?.type || '').toUpperCase();
  const conversationAiEnabled = conversation?.aiEnabled === true;

  const consumeOptimisticThinking = useCallback((messages, { messageId, role }) => {
    const result = consumeOptimisticThinkingForMessage({
      messages,
      pendingOptimisticIds: pendingOptimisticThinkingIdsRef.current,
      consumedByMessageId: consumedOptimisticThinkingByMessageIdRef.current,
      messageId,
      role
    });
    pendingOptimisticThinkingIdsRef.current = result.pendingOptimisticIds;
    consumedOptimisticThinkingByMessageIdRef.current = result.consumedByMessageId;
    return result.messages;
  }, []);

  const handleAssistantThinkingStart = useCallback((rawContent) => {
    const content = String(rawContent || '').trim();
    const shouldShowThinking = (
      conversationType === 'AI'
      || conversationAiEnabled
      || /(?:^|\s)@kapsaai\b/i.test(content)
    );
    if (!shouldShowThinking) return null;

    const optimisticId = `optimistic-thinking-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    const timestamp = toIsoTimestamp(Date.now());
    pendingOptimisticThinkingIdsRef.current.push(optimisticId);
    setAllMessages((previous) => [
      ...previous,
      {
        id: optimisticId,
        conversationId,
        senderId: 'kapsa-ai',
        senderDisplayName: 'Kapsa AI',
        content: '',
        role: 'assistant',
        threadParentId: null,
        threadReplyCount: 0,
        reactionCounts: null,
        widgetType: null,
        widgetData: null,
        widgetActionUserId: null,
        isComplete: false,
        createdAt: timestamp,
        timestamp
      }
    ]);
    return optimisticId;
  }, [conversationAiEnabled, conversationId, conversationType]);

  // Reset messages and sync query results when conversation changes or data arrives
  useEffect(() => {
    if (lastHydratedConversationRef.current === conversationId) return;
    lastHydratedConversationRef.current = conversationId;
    setActiveThread(null);
    setAllMessages([]);
    pendingOptimisticThinkingIdsRef.current = [];
    consumedOptimisticThinkingByMessageIdRef.current = new Map();
    hydrationRetryByConversationRef.current.set(conversationId, 0);
    refetch({ conversationId, limit: 50 }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('GET_MESSAGES_PAGINATED initial fetch failed', err);
    });
  }, [conversationId, refetch]);

  // Bounded retry with exponential backoff when a fetch ERRORS and the panel
  // is still empty. Up to 3 attempts at 300ms, 1s, 3s. A genuinely empty
  // conversation that returns successfully (no error) is treated as final and
  // does not trigger retries — otherwise "No messages yet" flickers and the
  // backend takes avoidable GET_MESSAGES_PAGINATED traffic.
  useEffect(() => {
    if (loading) return undefined;
    if (allMessages.length > 0) {
      hydrationRetryByConversationRef.current.set(conversationId, 0);
      return undefined;
    }
    if (!error) return undefined;

    const attempt = hydrationRetryByConversationRef.current.get(conversationId) || 0;
    if (attempt >= HYDRATION_RETRY_BACKOFFS_MS.length) return undefined;

    const delay = HYDRATION_RETRY_BACKOFFS_MS[attempt];
    hydrationRetryByConversationRef.current.set(conversationId, attempt + 1);

    const retryTimer = setTimeout(() => {
      refetch({ conversationId, limit: 50 }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`GET_MESSAGES_PAGINATED retry ${attempt + 1} failed`, err);
      });
    }, delay);

    return () => clearTimeout(retryTimer);
  }, [allMessages.length, conversationId, error, loading, refetch]);

  useEffect(() => {
    const msgs = pageInfo?.messages;
    if (Array.isArray(msgs)) {
      const serverMessages = reconcileMessagesWithServer(msgs, conversationId);
      // Merge by id, preferring server copies, preserving local-only
      // messages (subscription-delivered, optimistic AI). The companion
      // cache write in ON_NEW_MESSAGE is the primary fix; this is the
      // seatbelt for any future ingress path that forgets it.
      setAllMessages((previous) => mergeServerAndLocalMessages(serverMessages, previous));
    }
  }, [pageInfo, conversationId]);

  // KAP-309 — `?msg=<id>` deeplink. UnifiedChatPanel owns URL parsing,
  // dedup, and history-strip; MessageListV2 owns the actual scroll
  // (virtuoso.scrollToIndex) + the pulse highlight, so virtualized targets
  // are mounted before we try to attach the `data-msg-anchor` attribute.
  // We surface a `pendingDeeplinkMsgId` to the list; the list calls back
  // `handleDeeplinkScrolled` once it has scrolled (or given up after
  // ~1.2s of trying to mount the index).
  const pendingDeeplinkMsgId = (() => {
    const parsed = parseMessageDeepLink(location.search);
    if (!parsed) return null;
    if (allMessages.length === 0) return null;
    const dedupeKey = `${conversationId}::${parsed}`;
    if (lastDeepLinkScrolledRef.current === dedupeKey) return null;
    if (!allMessages.some((m) => m.id === parsed)) return null;
    return parsed;
  })();

  const handleDeeplinkScrolled = useCallback((scrolledId) => {
    lastDeepLinkScrolledRef.current = `${conversationId}::${scrolledId}`;
    // Strip ?msg= from the URL without a navigation so back/forward and
    // any re-render don't re-trigger the scroll.
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(MSG_PARAM);
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (_) {
      /* URL parsing failed — leave param in place rather than breaking nav. */
    }
  }, [conversationId]);

  useEffect(() => {
    if (!confirmPollUntilMs || Date.now() >= confirmPollUntilMs) return undefined;

    const poll = () => {
      refetch({ conversationId, limit: 50 }).catch(() => {});
    };

    poll();
    const intervalId = window.setInterval(() => {
      if (Date.now() >= confirmPollUntilMs) {
        window.clearInterval(intervalId);
        setConfirmPollUntilMs(0);
        return;
      }
      poll();
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [confirmPollUntilMs, conversationId, refetch]);

  // Mark-read is owned by WorkspaceContext's auto-mark-read effect; do not
  // duplicate the mutation here.

  // Fetch older messages
  const handleFetchMore = useCallback(() => {
    if (!hasMore || !nextCursor || loading) return;
    fetchMore({
      variables: { cursor: nextCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getMessagesPaginated) return prev;
        const oldMsgs = prev?.getMessagesPaginated?.messages || [];
        const newMsgs = fetchMoreResult.getMessagesPaginated.messages || [];
        // Deduplicate
        const existingIds = new Set(oldMsgs.map((m) => m.id));
        const uniqueNew = newMsgs.filter((m) => !existingIds.has(m.id));
        return {
          getMessagesPaginated: {
            ...fetchMoreResult.getMessagesPaginated,
            messages: [...uniqueNew, ...oldMsgs]
          }
        };
      }
    });
  }, [hasMore, nextCursor, loading, fetchMore]);

  // ── Subscriptions ────────────────────────────────────────────────────────────

  // New messages
  useSubscription(ON_NEW_MESSAGE, {
    variables: { conversationId: conversation.id },
    onData: ({ data: { data } }) => {
      const newMsg = data?.onNewMessage;
      if (!newMsg) return;
      // Map old schema fields to new schema if needed
      const normalizedMsg = {
        ...newMsg,
        senderId: newMsg.senderId || newMsg.senderUsername,
        createdAt: toIsoTimestamp(newMsg.createdAt || newMsg.timestamp),
        timestamp: toIsoTimestamp(newMsg.timestamp || newMsg.createdAt),
        threadParentId: newMsg.threadParentId || null,
        threadReplyCount: newMsg.threadReplyCount || 0,
        reactionCounts: parseReactionCounts(newMsg.reactionCounts),
        widgetType: newMsg.widgetType || null,
        widgetData: newMsg.widgetData || null,
        widgetActionUserId: newMsg.widgetActionUserId || null
      };
      setAllMessages((prev) => {
        let base = prev;
        if (normalizedMsg.role === 'assistant' || normalizedMsg.role === 'system') {
          base = consumeOptimisticThinking(base, {
            messageId: normalizedMsg.id,
            role: normalizedMsg.role
          });
        }
        const idx = base.findIndex((m) => m.id === normalizedMsg.id);
        const isNewMessage = idx < 0;
        let next;
        if (idx < 0) {
          next = [...base, normalizedMsg];
        } else {
          next = [...base];
          next[idx] = mergeOnNewMessageIntoExisting(base[idx], normalizedMsg, newMsg);
        }

        // Keep main timeline parent metadata in sync when threaded replies arrive.
        if (isNewMessage && normalizedMsg.threadParentId) {
          return applyThreadParentPatch(next, normalizedMsg);
        }
        return next;
      });

      // Also reflect subscription-delivered messages into the Apollo cache for
      // GET_MESSAGES_PAGINATED via the shared `mergeMessageIntoCache` helper.
      // The helper also applies the thread-parent metadata patch for new
      // thread replies — without that, the receiver's "N replies" indicator
      // on the parent message would not update until a hard refresh
      // (`mergeServerAndLocalMessages` server-prefers-on-id and would
      // overwrite the locally-patched parent on the next Apollo re-emit).
      // Surfaced 2026-05-15 by BH.
      try {
        const existing = apolloClient.readQuery({
          query: GET_MESSAGES_PAGINATED,
          variables: { conversationId: conversation.id, limit: 50 }
        });
        if (existing?.getMessagesPaginated) {
          apolloClient.writeQuery({
            query: GET_MESSAGES_PAGINATED,
            variables: { conversationId: conversation.id, limit: 50 },
            data: {
              getMessagesPaginated: {
                ...existing.getMessagesPaginated,
                messages: mergeMessageIntoCache(
                  existing.getMessagesPaginated.messages || [],
                  newMsg
                )
              }
            }
          });
        }
      } catch (_err) {
        // Cache miss is fine — local state is already updated above.
      }

      // KAP-311.2 cross-user sync: when a pin/unpin or forward broadcast lands
      // here from another user, the cached `Message.pinnedAt` flips via the
      // mergeMessageIntoCache call above. But `listPinnedMessages` is a
      // separate cached query that does NOT update from that merge. If the
      // incoming pinnedAt differs from what listPinnedMessages currently
      // contains for this message, refetch the query so the banner stays in
      // lockstep. Only fires when something actually changed — pin badge
      // bubble updates on its own via the merge.
      try {
        const pinnedQuery = apolloClient.readQuery({
          query: LIST_PINNED_MESSAGES,
          variables: { conversationId: conversation.id }
        });
        const cached = pinnedQuery?.listPinnedMessages || [];
        const wasInList = cached.some((m) => m.id === newMsg.id);
        const shouldBeInList = !!newMsg.pinnedAt && !newMsg.deletedAt;
        if (wasInList !== shouldBeInList) {
          apolloClient.refetchQueries({ include: [LIST_PINNED_MESSAGES] });
        }
      } catch (_err) {
        // Cache miss → banner's own query will hydrate from network.
      }

      // Mark read if the chat is focused
      markRead(conversation.id);
    }
  });

  // Message updates (e.g. streaming content, two-phase AI responses)
  useSubscription(ON_MESSAGE_UPDATE, {
    variables: { conversationId: conversation.id },
    onData: ({ data: { data } }) => {
      const update = data?.onMessageUpdate;
      if (!update) return;

      setAllMessages((prev) => {
        const existingMessage = prev.find((message) => message.id === update.messageId);
        const base = consumeOptimisticThinking(prev, {
          messageId: update.messageId,
          role: existingMessage?.role
        });
        return applyMessageUpdateToMessages(base, update);
      });

      if (update.isComplete === true) {
        refetch().catch(() => {});
      }
    }
  });

  // Reaction updates
  useSubscription(ON_REACTION_UPDATE, {
    variables: { conversationId: conversation.id },
    onData: ({ data: { data } }) => {
      const update = data?.onReactionUpdate;
      if (!update) return;
      setAllMessages((prev) =>
        prev.map((m) =>
          m.id === update.id ? { ...m, reactionCounts: parseReactionCounts(update.reactionCounts) } : m
        )
      );
    }
  });

  // ── Thread handling ──────────────────────────────────────────────────────────

  const openThread = useCallback(
    (messageId) => {
      const msg = allMessages.find((m) => m.id === messageId);
      if (msg) setActiveThread(msg);
    },
    [allMessages]
  );

  const closeThread = useCallback(() => setActiveThread(null), []);

  const handleConfirmationSubmitted = useCallback(() => {
    // Keep polling long enough to survive occasional slow tool calls
    // when realtime subscription updates are delayed or dropped.
    const pollWindowMs = 90000;
    const nextPollUntil = Date.now() + pollWindowMs;
    setConfirmPollUntilMs((current) => Math.max(current, nextPollUntil));
    refetch({ conversationId, limit: 50 }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('GET_MESSAGES_PAGINATED confirmation poll failed', err);
    });
  }, [conversationId, refetch]);

  const handleRetryHydration = useCallback(() => {
    hydrationRetryByConversationRef.current.set(conversationId, 0);
    refetch({ conversationId, limit: 50 }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('GET_MESSAGES_PAGINATED manual retry failed', err);
    });
  }, [conversationId, refetch]);

  // Channel name for composer placeholder
  const channelName = conversation.type === 'CHANNEL' ? conversation.title : null;

  if (loading && allMessages.length === 0) {
    return (
      <div className="unified-chat-panel">
        <ChatHeader conversation={conversation} />
        <div className="loading">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="unified-chat-panel">
      <ChatHeader conversation={conversation} />

      <div className="unified-chat-body">
        <div className={`unified-chat-main ${activeThread ? 'with-thread' : ''}`}>
          <PinnedMessagesBanner
            conversationId={conversation.id}
            onJumpToMessage={(messageId) => {
              // Navigate to the same conversation with ?msg=... so the
              // existing deep-link IIFE picks the target up, MessageListV2
              // scrolls + pulse-highlights, then handleDeeplinkScrolled
              // strips the param. Stays inside react-router so back/forward
              // works.
              navigate(
                `/conversations/${encodeURIComponent(conversation.id)}?${MSG_PARAM}=${encodeURIComponent(messageId)}`,
                { replace: true }
              );
            }}
            onUnpinMessage={handleUnpinMessage}
          />
          <MessageListV2
            messages={allMessages}
            hasMore={hasMore}
            loading={loading}
            error={error}
            onRetry={handleRetryHydration}
            onFetchMore={handleFetchMore}
            onConfirmationSubmitted={handleConfirmationSubmitted}
            currentUserId={currentUserId}
            isAdminViewer={isAdminViewer}
            onOpenThread={openThread}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onPinMessage={handlePinMessage}
            onUnpinMessage={handleUnpinMessage}
            onForwardMessage={handleOpenForwardModal}
            onBookmarkMessage={handleBookmarkMessage}
            onUnbookmarkMessage={handleUnbookmarkMessage}
            bookmarkedMessageIds={bookmarkedMessageIds}
            quickReactTokens={quickReactTokens}
            scrollToMessageId={pendingDeeplinkMsgId}
            onScrolledToMessageId={handleDeeplinkScrolled}
          />
          <MessageComposer
            conversationId={conversation.id}
            channelName={channelName}
            onAssistantThinkingStart={handleAssistantThinkingStart}
          />
        </div>

        {activeThread && (
          <ThreadPanel
            parentMessage={activeThread}
            conversationId={conversation.id}
            currentUserId={currentUserId}
            channelName={channelName}
            onClose={closeThread}
          />
        )}
      </div>

      {pendingForwardMessage && (
        <ForwardMessageModal
          sourceMessage={pendingForwardMessage}
          onClose={handleCloseForwardModal}
          onSubmit={handleForwardSubmit}
        />
      )}
    </div>
  );
}

export default UnifiedChatPanel;
